import { RecipeDocument, type RecipeQuery } from '@mdvip/shared/graphql/generated/graphql';
import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import { useQuery } from '@tanstack/react-query';

export type RecipeDetail = RecipeQuery['getRecipeByVendorId'];

/**
 * One recipe's cache key, keyed by vendor id. Shares the `['recipes']` prefix with the gallery's
 * `recipesKey` so a future catalog-wide invalidation reaches both, but carries its own `'detail'`
 * segment so paging the gallery never evicts a fetched recipe.
 */
export const recipeKey = (id: string) => ['recipes', 'detail', id] as const;

/**
 * One recipe by its vendor id, for the detail page.
 *
 * No `Failure` variant to handle: `getRecipeByVendorId` is a pure read. A missing recipe is a
 * `NotFoundException` on the backend and arrives as `NOT_FOUND` in the standard GraphQL errors
 * envelope, alongside infrastructure codes like `SERVICE_UNAVAILABLE` — the caller tells them
 * apart with `extractGqlErrorCode`.
 */
export function useRecipe(id: string) {
  return useQuery({
    queryKey: recipeKey(id),
    queryFn: async () => {
      const data = await gqlRequest(RecipeDocument, { id });
      return data.getRecipeByVendorId;
    },
  });
}
